/**
 * RU ↔ FOREIGN stand pairing and prod→dev host rewrite for mobile dual-login.
 * Native TestFlight/debug seeds are dev.*; backends that still advertise zazer.mobi
 * must not send the WebView to production.
 */
export type StandEnv = 'dev' | 'feature' | 'release' | 'prod';

const STAND_PAIRS: Array<{ru: string; foreign: string; env: Exclude<StandEnv, 'prod'>}> = [
    {ru: 'dev.zazer.fun', foreign: 'dev.lgtest.fun', env: 'dev'},
    {ru: 'feature.zazer.fun', foreign: 'feature.lgtest.fun', env: 'feature'},
    {ru: 'release.zazer.fun', foreign: 'release.lgtest.fun', env: 'release'},
];

export class StandUrls {

    public static detect(haystack?: string): StandEnv {
        const hay = (haystack || StandUrls.contextHaystack()).toLowerCase();
        for (const pair of STAND_PAIRS) {
            if (hay.includes(pair.ru) || hay.includes(pair.foreign)) {
                return pair.env;
            }
        }
        return 'prod';
    }

    public static peerApi(stickyApi: string): string | undefined {
        const host = stickyApi.toLowerCase();
        for (const pair of STAND_PAIRS) {
            if (host.includes(pair.ru)) {
                return `https://${pair.foreign}`;
            }
            if (host.includes(pair.foreign)) {
                return `https://${pair.ru}`;
            }
        }
        return undefined;
    }

    public static isDev(): boolean {
        if (StandUrls.detect() === 'dev') {
            return true;
        }
        return StandUrls.contextHaystack().includes('dev.k8s.zazer.fun');
    }

    public static isDevOrFeature(): boolean {
        const env = StandUrls.detect();
        return env === 'dev' || env === 'feature';
    }

    /** On-screen native DBG overlay — dev / feature only. */
    public static debugLog(message: string): void {
        if (!message || !StandUrls.isDevOrFeature()) {
            return;
        }
        console.log(message);
        try {
            const w = window as any;
            if (w.webkit?.messageHandlers?.debugLog) {
                w.webkit.messageHandlers.debugLog.postMessage(message);
            } else if (typeof w.app?.debugLog === 'function') {
                w.app.debugLog(message);
            }
        } catch (e) {
            // ignore missing native bridge
        }
    }

    public static rewrite(url: string): string {
        if (!url) {
            return url;
        }
        if (StandUrls.detect() !== 'dev') {
            return url;
        }
        try {
            const parsed = new URL(url);
            const host = parsed.hostname.toLowerCase();
            if (host === 'zazer.mobi' || host === 'mland.mobi') {
                parsed.hostname = 'dev.zazer.fun';
            } else if (host === 'mirland.mobi' || host === 'lgtest.fun') {
                parsed.hostname = 'dev.lgtest.fun';
            } else {
                return url;
            }
            return parsed.toString();
        } catch (e) {
            return url;
        }
    }

    private static contextHaystack(): string {
        if (typeof window === 'undefined') {
            return '';
        }
        const native = window.__AOF_NATIVE_BOOTSTRAP__;
        return [
            window.location.hostname,
            native?.apiBaseUrl,
            native?.frontBaseUrl,
            native?.peerApiBaseUrl,
            native?.peerFrontBaseUrl,
        ].filter(Boolean).join(' ');
    }
}
